import { formatCurrency } from "@shared/utils/format";
import { BuyWidget } from "@solomon-apps/shared";
import {
    ArrowShortUpRightIcon,
    WalletIcon
} from "@solomon-labs/design/components/icons";
import {
    Button,
    Empty,
    EmptyContent,
    EmptyHeader,
    Skeleton,
    Text,
    TextSkeleton
} from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React from "react";

import { RAYDIUM_CLMM_LABEL } from "@/hooks/yaas/lpTypes";
import { TokenSelector } from "@/pages/USDv/components/TokenSelector";

import { YaasEarnProjectionCTA } from "../components/YaasEarnProjectionCTA";
import type { useYaasRetailFlow } from "../flow/useYaasRetailFlow";
import { breakdownIconFor } from "../helpers/breakdownIcon";
import type { ResolvedView } from "../state/types";

/**
 * Right-column preview surface — what we show when the user has no
 * active position. An unknown balance shows a skeleton; loaded balances
 * select one of two surfaces keyed off `landingVariant`:
 *
 *   "swap-no-stables" | "swap-from-stables"
 *      → BuyWidget (no Back — there's no projection to navigate to)
 *
 *   "projection" (default)
 *      → YaasEarnProjectionCTA (the enrollment-pitch card)
 *
 * `isBuyingUSDv` is hoisted to the top of the page's AnimatePresence
 * so the BuyWidget swap surface doesn't repeat inside this branch.
 */
export function PreviewLanding({
    resolved,
    isSquads = false,
    yaasApy,
    onEnable,
    onGetMoreUsdv,
    onBuyUsdv,
    flow,
    balanceError,
    onRetryBalance,
    onSwapComplete
}: {
    resolved: ResolvedView;
    isSquads?: boolean;
    yaasApy: number | null;
    /** Runs 3s after the inline BuyWidget confirms a swap. This surface
     *  has no Back link — correctly, while the wallet holds no USDv —
     *  but the moment the buy lands that stops being true, so refreshing
     *  balances here is what moves the variant on to the projection. */
    onSwapComplete: () => void;
    /** Fires when the projection CTA's primary button is clicked AND
     *  the user is connected and not in "go-to-buy" mode. Wires the
     *  Enable Rewards form open on the page. */
    onEnable: () => void;
    /** Fires from the Get More USDv affordance on the projection CTA. */
    onGetMoreUsdv: () => void;
    /** Used when `previewPrimaryGoesToBuy` (apply-below-min demo) — the
     *  primary CTA fires the buy-USDv action instead of starting the
     *  apply flow. */
    onBuyUsdv: () => void;
    /** Used to call `flow.connectWallet` when the user is disconnected
     *  and clicks the projection CTA's primary button. */
    flow: ReturnType<typeof useYaasRetailFlow>;
    /** A failed initial fetch stays unknown and offers recovery. */
    balanceError?: string | null;
    onRetryBalance?: () => void;
}): React.ReactElement {
    const inSwap =
        resolved.landingVariant === "swap-no-stables" ||
        resolved.landingVariant === "swap-from-stables";

    if (inSwap && isSquads) {
        return (
            <Empty className="items-start p-0 text-left">
                <EmptyHeader className="items-start text-left">
                    <Text variant="body" render={<h2 />}>
                        No positions found
                    </Text>
                    <Text variant="small" className="text-foreground-muted">
                        Hold at least{" "}
                        <Text variant="number" render={<span />}>
                            {formatCurrency(resolved.minBalanceExact)}
                        </Text>{" "}
                        in USDv across supported positions to start earning.
                    </Text>
                </EmptyHeader>
                <div className="flex w-full flex-col gap-3">
                    <Text variant="small" className="text-foreground-muted">
                        Supported positions
                    </Text>
                    <ul className="flex flex-col gap-4">
                        <li className="flex items-center gap-3">
                            <WalletIcon
                                aria-hidden="true"
                                className="size-6 shrink-0 text-foreground-muted"
                            />
                            <div className="flex flex-col gap-1">
                                <Text variant="body">Address USDv</Text>
                                <Text
                                    variant="small"
                                    className="text-foreground-muted"
                                >
                                    USDv held in your Squads vault
                                </Text>
                            </div>
                        </li>
                        {/* One row for every Raydium CLMM USDv pool: the
                            pool list lives in Jachin's catalog, so the app
                            names the protocol rather than a pool. */}
                        <li className="flex items-center gap-3">
                            {breakdownIconFor(
                                {
                                    pair: RAYDIUM_CLMM_LABEL,
                                    protocol: "Raydium",
                                    type: "DEX LP"
                                },
                                "size-6 shrink-0"
                            )}
                            <div className="flex flex-col gap-1">
                                <Text variant="body">{RAYDIUM_CLMM_LABEL}</Text>
                                <Text
                                    variant="small"
                                    className="text-foreground-muted"
                                >
                                    USDv in eligible liquidity pool positions
                                </Text>
                            </div>
                        </li>
                    </ul>
                </div>
                <EmptyContent className="items-start">
                    <Button
                        onClick={onBuyUsdv}
                        trailing={<ArrowShortUpRightIcon />}
                    >
                        Get More USDv to Earn
                    </Button>
                </EmptyContent>
            </Empty>
        );
    }
    if (inSwap) {
        return (
            <motion.div
                key="preview-swap"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{
                    opacity: { duration: 0.3, ease: "easeOut" },
                    y: { duration: 0.3, ease: "easeOut" }
                }}
            >
                {/* No enrol CTA here. Both swap variants mean the wallet
                    holds no USDv, and there is nothing to start earning on
                    until the swap completes. Offering it anyway sends the
                    reader to a form that cannot do anything for them. */}
                <BuyWidget
                    canFlip={resolved.isWalletConnected}
                    TokenSelectorComponent={TokenSelector}
                    onSuccessReturn={onSwapComplete}
                />
            </motion.div>
        );
    }

    if (resolved.previewPosition === null) {
        if (balanceError) {
            return (
                <div className="flex flex-col items-start gap-4">
                    <Text variant="body" role="status">
                        USDv balances unavailable
                    </Text>
                    {onRetryBalance && (
                        <Button onClick={onRetryBalance}>Try again</Button>
                    )}
                </div>
            );
        }
        return (
            <div
                role="status"
                aria-label="Loading USDv balances"
                aria-busy="true"
                className="flex flex-col gap-4"
            >
                <div className="border border-line-base p-3 md:p-6 flex flex-col gap-2">
                    <TextSkeleton variant="small" className="w-40" />
                    <TextSkeleton variant="number-lg" className="w-60" />
                </div>
                <div className="border border-line-base p-3 md:p-6 flex flex-col gap-2">
                    <TextSkeleton variant="small" className="w-24" />
                    <TextSkeleton variant="number-lg" className="w-40" />
                </div>
                <Skeleton className="h-12 w-full md:w-48" />
            </div>
        );
    }

    return (
        <motion.div
            key={`preview-${
                resolved.isWalletConnected ? "connected" : "disconnected"
            }`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
                opacity: { duration: 0.3, ease: "easeOut" },
                y: { duration: 0.3, ease: "easeOut" }
            }}
        >
            <YaasEarnProjectionCTA
                position={resolved.previewPosition}
                balanceLabel={resolved.previewBalanceLabel}
                primaryLabel={resolved.previewPrimaryLabel}
                primaryDisabled={resolved.previewPrimaryDisabled}
                primaryArrow={resolved.previewPrimaryArrow}
                // For a multisig the buy CTA leaves the app for Squads' trade page.
                primaryLinkOut={isSquads && resolved.previewPrimaryGoesToBuy}
                showGetMoreUsdv={resolved.previewShowGetMoreUsdv && !isSquads}
                yaasApy={yaasApy}
                onEnable={
                    resolved.previewPrimaryGoesToBuy
                        ? onBuyUsdv
                        : resolved.isWalletConnected
                          ? onEnable
                          : flow.connectWallet
                }
                onGetMoreUsdv={onGetMoreUsdv}
            />
        </motion.div>
    );
}
